import React, { useState, useEffect } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow, SubRow,
    InlineInput, AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';

const DUMMY_DATA = {
    question: 'How likely are you to recommend our platform to a friend or colleague? 🚀',
    settings: { minValue: 0, maxValue: 10, minLabel: 'Not at all likely', maxLabel: 'Extremely likely', description: true, descText: '0 = would not recommend at all, 10 = would definitely recommend. This is a Net Promoter Score (NPS) question.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const RatingScale = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [settings, setSettings] = useState({
        minValue: 1,
        maxValue: 10,
        minLabel: 'Not at all likely',
        maxLabel: 'Extremely likely',
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            } else {
                alert('Failed to improve with AI. Please try again.');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to improve with AI. Please try again.');
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };

    const minVal = Math.max(0, Number(settings.minValue) ?? 1);
    const maxValRaw = Math.min(100, Number(settings.maxValue) ?? 10);
    const maxVal = maxValRaw > minVal ? maxValRaw : minVal + 1;
    const scaleNumbers = Array.from(
        { length: Math.max(0, maxVal - minVal + 1) },
        (_, i) => minVal + i
    );

    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                {/* Minimum value */}
                <SettingsRow label="Minimum value" description="Lowest number on the scale">
                    <InlineInput
                        type="number"
                        value={settings.minValue}
                        onChange={(e) => {
                            const v = Math.max(0, Math.min(99, Number(e.target.value) || 0));
                            set('minValue', v);
                            if (Number(settings.maxValue) <= v) set('maxValue', v + 1);
                        }}
                        min={0}
                        max={99}
                    />
                </SettingsRow>

                {/* Maximum value */}
                <SettingsRow label="Maximum value" description="Highest number on the scale">
                    <InlineInput
                        type="number"
                        value={settings.maxValue}
                        onChange={(e) => {
                            const v = Math.min(100, Math.max(1, Number(e.target.value) || 10));
                            set('maxValue', v);
                            if (Number(settings.minValue) >= v) set('minValue', v - 1);
                        }}
                        min={1}
                        max={100}
                    />
                </SettingsRow>

                {/* Min label */}
                <SettingsRow label="Low score label">
                    <InlineInput
                        value={settings.minLabel}
                        onChange={(e) => set('minLabel', e.target.value)}
                        placeholder='e.g. "Not at all likely"'
                    />
                </SettingsRow>

                {/* Max label */}
                <SettingsRow label="High score label">
                    <InlineInput
                        value={settings.maxLabel}
                        onChange={(e) => set('maxLabel', e.target.value)}
                        placeholder='e.g. "Extremely likely"'
                    />
                </SettingsRow>

                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    const left = (
        <>
        <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
            {settings.imageUrl && (
                <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
            )}
            <div className={styles.questionContent}>
                <div className={styles.questionLabelRow}>
                    <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                    <span className={styles.aiActions}>
                        <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                        {previousQuestion !== null && (
                            <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                        )}
                    </span>
                </div>
                {editingQuestion ? (
                    <input
                        className={styles.questionInput}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="How likely are you to recommend this to a friend?"
                        autoFocus
                        onBlur={closeQuestionEditor}
                        onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                    />
                ) : (
                    <Tooltip title="Click to edit question" placement="top">
                        <div
                            className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                            role="button"
                            tabIndex={0}
                            onClick={openQuestionEditor}
                            onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                        >
                            {question || 'Type your question here...'}
                        </div>
                    </Tooltip>
                )}

                {settings.description && (
                    <div className={styles.questionDescription}>
                        <div className={styles.questionLabelRow}>
                            <span className={styles.descLabel}>Description</span>
                            <span className={styles.aiActions}>
                                <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                {previousDescText !== null && (
                                    <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                )}
                            </span>
                        </div>
                        {editingDesc ? (
                            <input
                                className={styles.descInput}
                                value={settings.descText}
                                onChange={(e) => set('descText', e.target.value)}
                                placeholder="Type description here..."
                                autoFocus
                                onBlur={closeDescEditor}
                                onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                            />
                        ) : (
                            <Tooltip title="Click to edit description" placement="top">
                                <div
                                    className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openDescEditor}
                                    onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                >
                                    {settings.descText || 'Type description here...'}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* Scale Preview */}
                <div className={styles.scalePreview}>
                    <div className={styles.scaleNumbers}>
                        {scaleNumbers.map((num) => (
                            <span key={num} className={styles.scaleNumberBadge}>{num}</span>
                        ))}
                    </div>
                    <div className={styles.scaleLabels}>
                        <span className={styles.scaleLabel}>{settings.minLabel || 'Low'}</span>
                        <span className={styles.scaleLabel}>{settings.maxLabel || 'High'}</span>
                    </div>
                </div>
            </div>
        </div>
        <div className={`${styles.infoCard} ${styles.infoCardTeal}`}>
            <div className={styles.infoCardLabel}>How Rating Scale works</div>
            <p className={styles.infoCardText}>
                Set the minimum and maximum values and optional labels. Participants pick one number on the scale. Results show the distribution and average.
            </p>
        </div>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'rating-scale',
                title: question || 'Rating Scale',
                content: { question, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<SlidersHorizontal size={20} />}
            color="var(--cat-teal)"
            title="Rating Scale"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                const min = Math.max(0, Math.min(99, Number(settings.minValue) || 1));
                const max = Math.min(100, Math.max(1, Number(settings.maxValue) || 10));
                const finalMin = Math.min(min, max - 1);
                const finalMax = Math.max(max, finalMin + 1);
                saveContent({
                    question,
                    settings: {
                        ...settings,
                        minValue: finalMin,
                        maxValue: finalMax,
                    },
                });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default RatingScale;
